import { AxiosRequestConfig } from 'axios';
import { api } from '..';
import { MULTIPART_HEADERS } from '../../helpers';
import {
  AuthUserResponsePartner,
  CustomerLocationEntity,
  DetailsBrandsSalesStatisticResponse,
  DetailsSalesStatisticResponse,
  GetLocationsOverviewParams,
  IncompleteLocationEntity,
} from '../../types';
import { SimpleLocationWithTodayDataResponse } from '../../types/location.types';
import {
  MenuSignInProviderEntity,
  PartnerConfirmLocationInputData,
  PartnerCreateLocationInputData,
  PartnerCreateLocationManuallyInputData,
  PartnerUpdateAlreadyUsedMenuProvidersInputData,
} from '../../types/partner';
import {
  DateRangeParams,
  PaginationParams,
  ResponseType,
} from '../../types/shared.types';
import { OrdersOverviewPartnerLocationDetailsEntity } from '../../types/brandOwner';

export const getPartnerLocations = (
  params: PaginationParams<{ query?: string | null; verified?: boolean }>,
  config?: AxiosRequestConfig
) => {
  return api.get<ResponseType<SimpleLocationWithTodayDataResponse[]>>(
    '/v2/locations',
    {
      params,
      ...config,
    }
  );
};

export const getPartnerLocationsWithBrands = (
  params: PaginationParams<{ query?: string | null; verified?: boolean }>,
  config?: AxiosRequestConfig
) => {
  return api.get<ResponseType<SimpleLocationWithTodayDataResponse[]>>(
    '/v2/locations',
    {
      params,
      ...config,
    }
  );
};

export const getPartnerLocationDetails = (
  id: number | string,
  params: DateRangeParams
) => {
  return api.get<ResponseType<CustomerLocationEntity>>(`/v2/locations/${id}`, {
    params,
  });
};

export const getPartnerHome = (
  params: DateRangeParams<{
    locationIds?: Array<number | string>;
    brandIds?: Array<number | string>;
  }>
) => {
  return api.get<ResponseType<DetailsSalesStatisticResponse>>(
    `/v1/locations/home`,
    {
      params,
    }
  );
};

export const getPartnerHomeBrands = (
  params: DateRangeParams<{
    locationIds?: Array<number | string>;
    brandIds?: Array<number | string>;
  }>
) => {
  return api.get<ResponseType<DetailsBrandsSalesStatisticResponse[]>>(
    `/v1/locations/home-brands`,
    {
      params,
    }
  );
};

export const updatePartnerLocation = (
  id: number,
  data: PartnerCreateLocationInputData
) => {
  return api.put<ResponseType<AuthUserResponsePartner>>(
    `/v1/locations/${id}`,
    data
  );
};

export const deletePartnerLocation = (id: number) => {
  return api.delete<ResponseType<null>>(`/v1/locations/${id}`);
};

export const createPartnerLocation = (data: PartnerCreateLocationInputData) =>
  api.post<ResponseType<IncompleteLocationEntity>>(
    '/v1/locations/create',
    data
  );

export const confirmPartnerLocation = (
  id: number,
  data: PartnerConfirmLocationInputData
) => {
  return api.put<ResponseType<IncompleteLocationEntity>>(
    `/v1/locations/${id}/confirm`,
    data
  );
};

export const createPartnerLocationManually = (
  data: PartnerCreateLocationManuallyInputData
) => {
  return api.post<ResponseType<IncompleteLocationEntity>>(
    `/v1/locations/create-manual`,
    data
  );
};

export const getPartnerMenuProviders = () => {
  return api.get<ResponseType<MenuSignInProviderEntity[]>>(
    `/v1/locations/menu-providers`
  );
};

export const updatePartnerAlreadyUsedMenuProviders = (
  id: number,
  data: PartnerUpdateAlreadyUsedMenuProvidersInputData
) => {
  return api.put<ResponseType<IncompleteLocationEntity>>(
    `/v1/locations/${id}/menu-providers`,
    data
  );
};

export const uploadPartnerBusinessLicense = (id: number, data: FormData) => {
  return api.post<ResponseType<string>>(
    `/v1/locations/${id}/business-license`,
    data,
    MULTIPART_HEADERS
  );
};

export const deletePartnerBusinessLicense = (id: number, imageUrl: string) => {
  return api.delete<ResponseType<null>>(`/v1/locations/${id}/business-license`, {
    data: { imageUrl },
  });
};

/**
 * Replaces the license of a location that is already complete, from Settings.
 * Takes the real location id, not the incomplete one the sign up routes above
 * use. Owner only: a manager gets 403. There is no delete - a license is
 * required, so it can only be replaced. Resolves to the new image URL.
 */
export const replacePartnerBusinessLicense = (
  locationId: number,
  data: FormData
) => {
  return api.post<ResponseType<string>>(
    `/v1/locations/${locationId}/settings/business-license`,
    data,
    MULTIPART_HEADERS
  );
};

export const completePartnerLocation = (id: number) => {
  return api.put<ResponseType<AuthUserResponsePartner>>(
    `/v1/locations/${id}/complete`
  );
};

export const getLocationsOverviewPartner = (
  id: string | number,
  params: GetLocationsOverviewParams
) =>
  api.get<ResponseType<OrdersOverviewPartnerLocationDetailsEntity>>(
    `/v2/locations/${id}/orders/overview`,
    {
      params,
    }
  );
